/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, formatINR, Tag } from '../console/ui';
import { useContainers } from '../../hooks/useContainers';
import { useVolumes } from '../../hooks/useVolumes';
import { useSpendByProject } from '../billing/useBilling';

interface Project {
    id: string;
    name: string;
    description?: string;
    created_at: string;
}

interface ProjectCardProps {
    project: Project;
    isCurrent?: boolean;
    onSwitch?: (project: Project) => void;
    onEdit: (project: Project) => void;
    onDelete: (project: Project) => void;
}

const Stat: React.FC<{ value: React.ReactNode; label: string; title?: string }> = ({ value, label, title }) => (
    <div className="flex flex-col gap-0.5 rounded-[10px] bg-ps-raised/60 px-3 py-2.5 text-xs text-ps-muted min-w-0" title={title}>
        <b className="text-lg font-semibold text-ps-text truncate">{value}</b>
        {label}
    </div>
);

// Per-project numbers only - pods, volumes and month-to-date spend of this
// project. The wallet balance is account-wide, so it is deliberately not shown
// here. Spend comes from /wallet/spend-by-project (one shared request for all
// cards); on older backends it's unavailable and the Running count shows instead.
const ProjectCard: React.FC<ProjectCardProps> = ({ project, isCurrent, onSwitch, onEdit, onDelete }) => {
    const { totalCount: pods, runningCount, isLoading: podsLoading } = useContainers(project.id);
    const { totalCount: volumes, isLoading: volumesLoading } = useVolumes(project.id);
    const { data: spend, isLoading: spendLoading } = useSpendByProject();
    const dash = <span className="text-ps-subtle">-</span>;
    const projectSpend = spend?.projects.find((p) => p.project_id === project.id)?.total ?? 0;
    const showSpend = spendLoading || !!spend;

    return (
        <article className="ps-card p-5 flex flex-col gap-4">
            <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                    <h2 className="m-0 text-[17px] font-medium text-ps-text truncate">
                        <Link to={`/projects/${project.id}`} className="hover:text-ps-accent">
                            {project.name}
                        </Link>
                    </h2>
                    <div className="text-ps-subtle text-[13px] mt-0.5">
                        Created {format(new Date(project.created_at), 'd MMM yyyy')} ·{' '}
                        {project.description ? (
                            <span className="text-ps-muted">{project.description}</span>
                        ) : (
                            <button type="button" className="ps-link" onClick={() => onEdit(project)}>
                                Add a description
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5 justify-end">
                    {isCurrent && <Tag tone="accent">Current</Tag>}
                    {!podsLoading && <Tag tone={runningCount > 0 ? 'ok' : 'off'}>{runningCount > 0 ? 'Active' : 'Idle'}</Tag>}
                </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
                <Stat value={podsLoading ? dash : pods} label="Pods" />
                {!showSpend && <Stat value={podsLoading ? dash : runningCount} label="Running" />}
                <Stat value={volumesLoading ? dash : volumes} label="Volumes" />
                {showSpend && (
                    <Stat
                        value={spend ? formatINR(projectSpend, spend.currency, projectSpend >= 1000 ? 0 : 2) : dash}
                        label={`Spend, ${format(new Date(), 'MMM')}`}
                        title="Month-to-date usage billed to your wallet for this project's pods, volumes and storage"
                    />
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                <Link to={`/projects/${project.id}`} className="ps-btn2 ps-btn-sm">
                    Open
                </Link>
                {onSwitch && (
                    <Button variant="secondary" size="sm" disabled={isCurrent} onClick={() => onSwitch(project)}>
                        {isCurrent ? 'Current project' : 'Switch to project'}
                    </Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => onEdit(project)} className="ml-auto">
                    Edit
                </Button>
                <Button variant="secondary" size="sm" onClick={() => onDelete(project)} aria-label={`Delete ${project.name}`}>
                    Delete
                </Button>
            </div>
        </article>
    );
};

export default ProjectCard;
